import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  Timestamp,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../config/firebase/firebaseConfig';
import { TermsModel } from '../types/terms';
import { highestVersion, nextPatchVersion } from '../helpers/termsVersion';

const TERMS_COLLECTION = 'terms';

/**
 * Reads and publishes the terms & conditions the mobile app shows.
 *
 * Publishing appends a new document rather than editing the existing one: a
 * user who accepted 1.0.2 has that exact string on their profile, so the text
 * they agreed to has to stay retrievable.
 */
export class TermsService {
  /** Every published version, newest first. */
  static async loadTermsHistory(): Promise<TermsModel[]> {
    try {
      const termsQuery = query(
        collection(db, TERMS_COLLECTION),
        orderBy('updatedAt', 'desc')
      );

      const snapshot = await getDocs(termsQuery);

      return snapshot.docs.map((snap) => {
        const data = snap.data();
        return {
          id: snap.id,
          version: data.version ?? '',
          htmlContent: data.htmlContent ?? '',
          createdAt: (data.createdAt as Timestamp) ?? null,
          updatedAt: (data.updatedAt as Timestamp) ?? null,
        };
      });
    } catch (error) {
      console.error('Error loading terms history:', error);
      throw error;
    }
  }

  /**
   * The version currently served to the app: newest by `updatedAt`, matching
   * the mobile query in terms_repo.dart.
   */
  static async getLatestTerms(): Promise<TermsModel | null> {
    const history = await this.loadTermsHistory();
    return history[0] ?? null;
  }

  /**
   * Publishes `htmlContent` as a new version with the patch bumped, e.g.
   * 1.0.2 -> 1.0.3. The first ever publish is 1.0.0.
   *
   * The new version becomes live to every app user the moment it is written,
   * and each user is re-prompted to accept because their stored
   * `termsAcceptedVersion` no longer matches.
   */
  static async publishNewVersion(htmlContent: string): Promise<TermsModel> {
    try {
      const history = await this.loadTermsHistory();
      const version = nextPatchVersion(
        highestVersion(history.map((terms) => terms.version))
      );

      // The version string doubles as the document id, so Firestore itself
      // rejects a duplicate if two admins publish at the same moment rather
      // than silently creating two docs claiming the same version.
      const docRef = doc(db, TERMS_COLLECTION, version);

      await runTransaction(db, async (transaction) => {
        const existing = await transaction.get(docRef);
        if (existing.exists()) {
          throw new Error(
            `Version ${version} already exists. Someone may have just published — reload and try again.`
          );
        }

        transaction.set(docRef, {
          version,
          htmlContent,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      });

      // Re-read so the caller gets server-resolved timestamps instead of the
      // nulls a pending serverTimestamp() returns locally.
      const saved = await getDoc(docRef);
      const data = saved.data();

      return {
        id: saved.id,
        version,
        htmlContent,
        createdAt: (data?.createdAt as Timestamp) ?? null,
        updatedAt: (data?.updatedAt as Timestamp) ?? null,
      };
    } catch (error) {
      console.error('Error publishing terms version:', error);
      throw error;
    }
  }

  /**
   * Documents published before this panel existed have no `createdAt`. Their
   * `updatedAt` is the closest honest record of when they went live, so it is
   * copied across; anything without either is left for the caller to spot.
   */
  static async backfillCreatedAt(): Promise<number> {
    try {
      const history = await this.loadTermsHistory();
      const legacy = history.filter((terms) => terms.createdAt === null);

      if (legacy.length === 0) return 0;

      const batch = writeBatch(db);
      legacy.forEach((terms) => {
        batch.update(doc(db, TERMS_COLLECTION, terms.id), {
          createdAt: terms.updatedAt ?? serverTimestamp(),
        });
      });

      await batch.commit();
      return legacy.length;
    } catch (error) {
      console.error('Error backfilling terms createdAt:', error);
      throw error;
    }
  }
}
